<template>
  <div class="pa-4">
    <v-row>
      <!-- Sidebar de navegação -->
      <v-col cols="12" md="3" class="d-none d-md-block">
        <div class="docs-nav">
          <div class="nav-label mb-3">NAVEGAÇÃO</div>
          <div class="nav-list">
            <div
              v-for="section in sections"
              :key="section.id"
              class="nav-item"
              :class="{ 'nav-item--active': activeSection === section.id }"
              @click="scrollTo(section.id)"
            >
              <v-icon x-small class="nav-item-icon">{{ section.icon }}</v-icon>
              <span class="nav-item-text">{{ section.title }}</span>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Conteúdo -->
      <v-col cols="12" md="9">

        <!-- Introdução -->
        <section id="introducao" class="doc-section mb-8">
          <div class="section-title mb-2">Introdução</div>
          <p class="doc-text mb-3">
            A Equipacare FIX API externa permite integrar sistemas de terceiros com dados de equipamentos,
            ordens de serviço e chamados de forma segura. Todos os dados são filtrados automaticamente
            pela instituição associada à sua chave de API.
          </p>
          <div class="d-flex flex-wrap">
            <v-chip small outlined color="#0F3565" class="mr-2">REST</v-chip>
            <v-chip small outlined color="#0F3565" class="mr-2">JSON</v-chip>
            <v-chip small outlined color="#0F3565">HTTPS</v-chip>
          </div>
        </section>

        <v-divider class="mb-8"></v-divider>

        <!-- Autenticação -->
        <section id="autenticacao" class="doc-section mb-8">
          <div class="section-title mb-2">Autenticação</div>
          <p class="doc-text mb-4">
            Inclua sua chave no header <code class="inline-code">X-Api-Key</code> em todas as requisições.
            Gere sua chave na aba <strong>Chaves de API</strong>.
          </p>

          <div class="code-label">Header obrigatório</div>
          <div class="code-block mb-4">
            <pre>X-Api-Key: eqp_live_sua_chave_aqui</pre>
          </div>

          <div class="code-label">Exemplo cURL</div>
          <div class="code-block mb-4">
            <pre>curl -X GET "{{ baseUrl }}api/external/v1/equipments" \
  -H "X-Api-Key: eqp_live_sua_chave_aqui" \
  -H "Accept: application/json"</pre>
          </div>

          <div class="info-notice">
            <v-icon small color="#0F3565" class="notice-icon">mdi-information-outline</v-icon>
            <span>Sua chave dá acesso apenas aos dados da sua instituição.</span>
          </div>
        </section>

        <v-divider class="mb-8"></v-divider>

        <!-- Erros -->
        <section id="erros" class="doc-section mb-8">
          <div class="section-title mb-2">Erros de Autenticação</div>

          <div class="doc-table mb-4">
            <table>
              <thead>
                <tr>
                  <th>HTTP</th>
                  <th>Código</th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code class="inline-code">401</code></td>
                  <td><code class="inline-code">invalid_api_key</code></td>
                  <td>Header ausente, formato inválido ou chave não encontrada</td>
                </tr>
                <tr>
                  <td><code class="inline-code">401</code></td>
                  <td><code class="inline-code">expired_api_key</code></td>
                  <td>Chave revogada ou expirada</td>
                </tr>
                <tr>
                  <td><code class="inline-code">429</code></td>
                  <td><code class="inline-code">rate_limit_exceeded</code></td>
                  <td>Limite de 60 requisições/minuto atingido</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="code-label">Resposta 401</div>
          <div class="code-block mb-4">
            <pre>{{ authErrorExample }}</pre>
          </div>

          <div class="code-label">Resposta 429</div>
          <div class="code-block">
            <pre>{{ rateLimitExample }}</pre>
          </div>
        </section>

        <v-divider class="mb-8"></v-divider>

        <!-- Rate Limiting -->
        <section id="rate-limit" class="doc-section mb-8">
          <div class="section-title mb-2">Rate Limiting</div>
          <p class="doc-text">
            Limite de <strong>60 requisições por minuto</strong> por chave (sliding window).
            Quando excedido, retorna HTTP <code class="inline-code">429</code> com o header
            <code class="inline-code">Retry-After</code> indicando quantos segundos aguardar.
          </p>
        </section>

        <v-divider class="mb-8"></v-divider>

        <!-- Paginação -->
        <section id="paginacao" class="doc-section mb-8">
          <div class="section-title mb-2">Paginação</div>
          <p class="doc-text mb-4">Todos os endpoints de listagem retornam dados paginados.</p>

          <div class="doc-table mb-4">
            <table>
              <thead>
                <tr>
                  <th>Parâmetro</th>
                  <th>Tipo</th>
                  <th>Padrão</th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code class="inline-code">page</code></td>
                  <td class="type-col">integer</td>
                  <td>1</td>
                  <td>Número da página (começa em 1)</td>
                </tr>
                <tr>
                  <td><code class="inline-code">page_size</code></td>
                  <td class="type-col">integer</td>
                  <td>20</td>
                  <td>Itens por página (máximo: 100)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="code-label">Estrutura da resposta</div>
          <div class="code-block">
            <pre>{{ paginationExample }}</pre>
          </div>
        </section>

        <v-divider class="mb-8"></v-divider>

        <!-- Equipamentos -->
        <section id="equipamentos" class="doc-section mb-8">
          <div class="section-title mb-4">Equipamentos</div>

          <div class="endpoint-block mb-6">
            <div class="endpoint-header mb-3">
              <span class="method-badge get">GET</span>
              <code class="endpoint-url">/api/external/v1/equipments</code>
            </div>
            <p class="doc-text mb-3">Lista equipamentos da instituição com filtros e paginação.</p>

            <div class="param-label mb-2">Parâmetros de query</div>
            <div class="doc-table mb-4">
              <table>
                <thead>
                  <tr><th>Parâmetro</th><th>Tipo</th><th>Descrição</th></tr>
                </thead>
                <tbody>
                  <tr v-for="p in equipmentParams" :key="p.name">
                    <td><code class="inline-code">{{ p.name }}</code></td>
                    <td class="type-col">{{ p.type }}</td>
                    <td>{{ p.desc }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="code-label">cURL</div>
            <div class="code-block mb-4">
              <pre>curl -X GET "{{ baseUrl }}api/external/v1/equipments?page=1&amp;page_size=20" \
  -H "X-Api-Key: eqp_live_sua_chave_aqui"</pre>
            </div>

            <div class="code-label">Resposta (200)</div>
            <div class="code-block">
              <pre>{{ equipmentListResponse }}</pre>
            </div>
          </div>

          <div class="endpoint-block">
            <div class="endpoint-header mb-3">
              <span class="method-badge get">GET</span>
              <code class="endpoint-url">/api/external/v1/equipments/{id}</code>
            </div>
            <p class="doc-text mb-3">Retorna um equipamento específico pelo ID.</p>

            <div class="code-label">cURL</div>
            <div class="code-block mb-4">
              <pre>curl -X GET "{{ baseUrl }}api/external/v1/equipments/123" \
  -H "X-Api-Key: eqp_live_sua_chave_aqui"</pre>
            </div>

            <div class="code-label">Resposta (200)</div>
            <div class="code-block mb-4">
              <pre>{{ equipmentSingleResponse }}</pre>
            </div>

            <div class="code-label">Erros possíveis</div>
            <div class="doc-table">
              <table>
                <tbody>
                  <tr>
                    <td style="width:80px"><code class="inline-code">404</code></td>
                    <td>Equipamento não encontrado ou não pertence à sua instituição</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <v-divider class="mb-8"></v-divider>

        <!-- Ordens de Serviço -->
        <section id="ordens" class="doc-section mb-8">
          <div class="section-title mb-4">Ordens de Serviço</div>

          <div class="endpoint-block">
            <div class="endpoint-header mb-3">
              <span class="method-badge get">GET</span>
              <code class="endpoint-url">/api/external/v1/service-orders</code>
            </div>
            <p class="doc-text mb-3">Lista ordens de serviço da instituição.</p>

            <div class="param-label mb-2">Parâmetros de query</div>
            <div class="doc-table mb-4">
              <table>
                <thead>
                  <tr><th>Parâmetro</th><th>Tipo</th><th>Descrição</th></tr>
                </thead>
                <tbody>
                  <tr v-for="p in serviceOrderParams" :key="p.name">
                    <td><code class="inline-code">{{ p.name }}</code></td>
                    <td class="type-col">{{ p.type }}</td>
                    <td>{{ p.desc }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="param-label mb-2">Valores de status</div>
            <div class="doc-table mb-4">
              <table>
                <thead><tr><th>Valor</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code class="inline-code">0</code></td><td>Aberta</td></tr>
                  <tr><td><code class="inline-code">1</code></td><td>Em andamento</td></tr>
                  <tr><td><code class="inline-code">2</code></td><td>Fechada</td></tr>
                  <tr><td><code class="inline-code">3</code></td><td>Cancelada</td></tr>
                </tbody>
              </table>
            </div>

            <div class="code-label">cURL</div>
            <div class="code-block mb-4">
              <pre>curl -X GET "{{ baseUrl }}api/external/v1/service-orders?status=0&amp;page=1" \
  -H "X-Api-Key: eqp_live_sua_chave_aqui"</pre>
            </div>

            <div class="code-label">Resposta (200)</div>
            <div class="code-block">
              <pre>{{ serviceOrderListResponse }}</pre>
            </div>
          </div>
        </section>

        <v-divider class="mb-8"></v-divider>

        <!-- Chamados -->
        <section id="chamados" class="doc-section mb-4">
          <div class="section-title mb-4">Chamados</div>

          <div class="endpoint-block">
            <div class="endpoint-header mb-3">
              <span class="method-badge get">GET</span>
              <code class="endpoint-url">/api/external/v1/calleds</code>
            </div>
            <p class="doc-text mb-3">Lista chamados da instituição.</p>

            <div class="param-label mb-2">Parâmetros de query</div>
            <div class="doc-table mb-4">
              <table>
                <thead>
                  <tr><th>Parâmetro</th><th>Tipo</th><th>Descrição</th></tr>
                </thead>
                <tbody>
                  <tr v-for="p in calledParams" :key="p.name">
                    <td><code class="inline-code">{{ p.name }}</code></td>
                    <td class="type-col">{{ p.type }}</td>
                    <td>{{ p.desc }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="param-label mb-2">Valores de prioridade</div>
            <div class="doc-table mb-4">
              <table>
                <thead><tr><th>Valor</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code class="inline-code">0</code></td><td>Baixa</td></tr>
                  <tr><td><code class="inline-code">1</code></td><td>Média</td></tr>
                  <tr><td><code class="inline-code">2</code></td><td>Alta</td></tr>
                  <tr><td><code class="inline-code">3</code></td><td>Urgente</td></tr>
                </tbody>
              </table>
            </div>

            <div class="code-label">cURL</div>
            <div class="code-block mb-4">
              <pre>curl -X GET "{{ baseUrl }}api/external/v1/calleds?priority=2&amp;page=1" \
  -H "X-Api-Key: eqp_live_sua_chave_aqui"</pre>
            </div>

            <div class="code-label">Resposta (200)</div>
            <div class="code-block">
              <pre>{{ calledListResponse }}</pre>
            </div>
          </div>
        </section>

        <v-divider class="mb-8"></v-divider>

        <!-- Cronograma -->
        <section id="cronograma" class="doc-section mb-4">
          <div class="section-title mb-4">Cronograma</div>

          <div class="endpoint-block mb-6">
            <div class="endpoint-header mb-3">
              <span class="method-badge get">GET</span>
              <code class="endpoint-url">/api/external/v1/schedule</code>
            </div>
            <p class="doc-text mb-3">
              Lista o cronograma completo de manutenção programada da instituição (preventivas, calibrações,
              segurança elétrica, qualificação/validação e treinamentos). Combina dois tipos de item,
              distinguidos pelo campo <code class="inline-code">source</code>:
            </p>
            <ul class="doc-text mb-3" style="padding-left:18px">
              <li><code class="inline-code">ServiceOrder</code> — ordens de serviço já abertas/agendadas.</li>
              <li><code class="inline-code">Planning</code> — planejamentos programados que ainda não geraram O.S.</li>
            </ul>
            <p class="doc-text mb-3">
              A data agendada é retornada no campo <code class="inline-code">scheduledDate</code> em ambos os casos.
              Os campos específicos de O.S. (<code class="inline-code">number</code>,
              <code class="inline-code">status</code>, etc.) vêm nulos para itens do tipo
              <code class="inline-code">Planning</code>, e vice-versa.
            </p>

            <div class="param-label mb-2">Parâmetros de query</div>
            <div class="doc-table mb-4">
              <table>
                <thead>
                  <tr><th>Parâmetro</th><th>Tipo</th><th>Descrição</th></tr>
                </thead>
                <tbody>
                  <tr v-for="p in scheduleParams" :key="p.name">
                    <td><code class="inline-code">{{ p.name }}</code></td>
                    <td class="type-col">{{ p.type }}</td>
                    <td>{{ p.desc }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="param-label mb-2">Tipos de manutenção programada</div>
            <div class="doc-table mb-4">
              <table>
                <thead><tr><th>Valor</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code class="inline-code">0</code></td><td>Treinamento</td></tr>
                  <tr><td><code class="inline-code">1</code></td><td>Segurança Elétrica</td></tr>
                  <tr><td><code class="inline-code">2</code></td><td>Preventiva</td></tr>
                  <tr><td><code class="inline-code">6</code></td><td>Calibração</td></tr>
                  <tr><td><code class="inline-code">7</code></td><td>Qualificação/Validação</td></tr>
                </tbody>
              </table>
            </div>

            <div class="code-label">cURL</div>
            <div class="code-block mb-4">
              <pre>curl -X GET "{{ baseUrl }}api/external/v1/schedule?page=1&amp;page_size=20" \
  -H "X-Api-Key: eqp_live_sua_chave_aqui"</pre>
            </div>

            <div class="code-label">Resposta (200)</div>
            <div class="code-block">
              <pre>{{ scheduleListResponse }}</pre>
            </div>
          </div>

          <div class="endpoint-block">
            <div class="endpoint-header mb-3">
              <span class="method-badge get">GET</span>
              <code class="endpoint-url">/api/external/v1/schedule/{id}</code>
            </div>
            <p class="doc-text mb-3">
              Retorna um item do cronograma pelo ID. Use o parâmetro
              <code class="inline-code">source</code> (<code class="inline-code">ServiceOrder</code>, padrão,
              ou <code class="inline-code">Planning</code>) para indicar a origem do ID, já que O.S. e
              planejamentos têm numerações independentes.
            </p>

            <div class="code-label">cURL</div>
            <div class="code-block mb-4">
              <pre>curl -X GET "{{ baseUrl }}api/external/v1/schedule/101?source=ServiceOrder" \
  -H "X-Api-Key: eqp_live_sua_chave_aqui"</pre>
            </div>

            <div class="code-label">Erros possíveis</div>
            <div class="doc-table">
              <table>
                <tbody>
                  <tr>
                    <td style="width:80px"><code class="inline-code">404</code></td>
                    <td>O.S. não encontrada, não é manutenção programada ou não pertence à sua instituição</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: 'Documentation',
  data() {
    return {
      activeSection: 'introducao',
      sections: [
        { id: 'introducao', title: 'Introdução', icon: 'mdi-book-outline' },
        { id: 'autenticacao', title: 'Autenticação', icon: 'mdi-key-outline' },
        { id: 'erros', title: 'Erros', icon: 'mdi-alert-circle-outline' },
        { id: 'rate-limit', title: 'Rate Limiting', icon: 'mdi-speedometer' },
        { id: 'paginacao', title: 'Paginação', icon: 'mdi-book-open-page-variant-outline' },
        { id: 'equipamentos', title: 'Equipamentos', icon: 'mdi-stethoscope' },
        { id: 'ordens', title: 'Ordens de Serviço', icon: 'mdi-wrench-outline' },
        { id: 'chamados', title: 'Chamados', icon: 'mdi-chat-alert-outline' },
        { id: 'cronograma', title: 'Cronograma', icon: 'mdi-calendar-clock-outline' },
      ],
      equipmentParams: [
        { name: 'page', type: 'integer', desc: 'Página (padrão: 1)' },
        { name: 'page_size', type: 'integer', desc: 'Itens por página (padrão: 20, máx: 100)' },
        { name: 'conservationState', type: 'string', desc: 'Filtro por estado de conservação' },
        { name: 'departmentId', type: 'integer', desc: 'Filtro por setor' },
        { name: 'unitId', type: 'integer', desc: 'Filtro por unidade' },
      ],
      serviceOrderParams: [
        { name: 'page', type: 'integer', desc: 'Página (padrão: 1)' },
        { name: 'page_size', type: 'integer', desc: 'Itens por página (padrão: 20, máx: 100)' },
        { name: 'status', type: 'integer', desc: 'Filtro por status (ver tabela)' },
        { name: 'type', type: 'integer', desc: 'Filtro por tipo de ordem' },
        { name: 'fromDate', type: 'datetime', desc: 'Data de criação inicial (ISO 8601)' },
        { name: 'toDate', type: 'datetime', desc: 'Data de criação final (ISO 8601)' },
      ],
      calledParams: [
        { name: 'page', type: 'integer', desc: 'Página (padrão: 1)' },
        { name: 'page_size', type: 'integer', desc: 'Itens por página (padrão: 20, máx: 100)' },
        { name: 'status', type: 'integer', desc: 'Filtro por status do chamado' },
        { name: 'priority', type: 'integer', desc: 'Filtro por prioridade (ver tabela)' },
        { name: 'fromDate', type: 'datetime', desc: 'Data de criação inicial (ISO 8601)' },
        { name: 'toDate', type: 'datetime', desc: 'Data de criação final (ISO 8601)' },
      ],
      scheduleParams: [
        { name: 'page', type: 'integer', desc: 'Página (padrão: 1)' },
        { name: 'page_size', type: 'integer', desc: 'Itens por página (padrão: 20, máx: 100)' },
        { name: 'type', type: 'integer', desc: 'Filtro por tipo de manutenção programada (ver tabela)' },
        { name: 'fromDate', type: 'datetime', desc: 'Data agendada inicial (ISO 8601)' },
        { name: 'toDate', type: 'datetime', desc: 'Data agendada final (ISO 8601)' },
      ],
      authErrorExample: JSON.stringify({ error: 'invalid_api_key' }, null, 2),
      rateLimitExample: JSON.stringify({ error: 'rate_limit_exceeded', retry_after_seconds: 30 }, null, 2),
      paginationExample: JSON.stringify({
        data: ['...'],
        pagination: { page: 1, pageSize: 20, totalItems: 142, totalPages: 8, hasMore: true },
      }, null, 2),
      equipmentListResponse: JSON.stringify({
        data: [{
          id: 1, name: 'Monitor Multiparamétrico', propertyCode: 'EQ-00123',
          serialNumber: 'SN-ABC123', model: 'ProCare 7000', manufacturer: 'Philips',
          location: 'UTI Adulto', conservationState: 'Bom', alienated: false,
          institutionId: 10, unitId: 2, departmentId: 5, criticality: 'Alta',
          lifeExpectancy: 10, purchaseDate: '2020-06-15T00:00:00Z',
          warrantyEndDate: '2023-06-15T00:00:00Z', createdAt: '2020-06-15T10:30:00Z',
        }],
        pagination: { page: 1, pageSize: 20, totalItems: 1, totalPages: 1, hasMore: false },
      }, null, 2),
      equipmentSingleResponse: JSON.stringify({
        id: 1, name: 'Monitor Multiparamétrico', propertyCode: 'EQ-00123',
        serialNumber: 'SN-ABC123', model: 'ProCare 7000', manufacturer: 'Philips',
        location: 'UTI Adulto', conservationState: 'Bom', alienated: false,
        institutionId: 10, unitId: 2, departmentId: 5, criticality: 'Alta',
        lifeExpectancy: 10, purchaseDate: '2020-06-15T00:00:00Z',
        warrantyEndDate: '2023-06-15T00:00:00Z', createdAt: '2020-06-15T10:30:00Z',
      }, null, 2),
      serviceOrderListResponse: JSON.stringify({
        data: [{
          id: 101, number: 'OS-2024-0542', name: 'Manutenção Preventiva',
          serviceOrderType: 0, status: 1, equipmentId: 1, departmentId: 5,
          departmentName: 'UTI Adulto', institutionId: 10, unitId: 2,
          unitName: 'Hospital Central', requester: 'João Silva',
          request: 'Equipamento apresentando falha na leitura de SpO2',
          location: 'UTI Adulto', dateTimeRequest: '2024-03-10T08:00:00Z',
          dateTimeAttend: '2024-03-10T09:30:00Z', closeDateTime: null,
          createdAt: '2024-03-10T08:00:00Z', updatedAt: '2024-03-10T09:30:00Z',
        }],
        pagination: { page: 1, pageSize: 20, totalItems: 1, totalPages: 1, hasMore: false },
      }, null, 2),
      calledListResponse: JSON.stringify({
        data: [{
          id: 55, number: 'CH-2024-0099', status: 1, priority: 2,
          category: 'Equipamento', equipmentId: 1, departmentId: 5,
          institutionId: 10, unitId: 2, serviceOrderId: 101,
          request: 'Alarme sonoro disparando continuamente',
          statusDescription: 'Técnico deslocado',
          createdAt: '2024-03-10T07:45:00Z', updatedAt: '2024-03-10T08:10:00Z',
        }],
        pagination: { page: 1, pageSize: 20, totalItems: 1, totalPages: 1, hasMore: false },
      }, null, 2),
      scheduleListResponse: JSON.stringify({
        data: [
          {
            source: 'ServiceOrder', id: 101, serviceOrderType: 2, equipmentId: 1,
            departmentId: 5, institutionId: 10, unitId: 2,
            scheduledDate: '2024-04-01T00:00:00Z', createdAt: '2024-03-01T10:00:00Z',
            number: 542, name: 'Manutenção Preventiva Trimestral', status: 0,
            workshopId: 3, responsibleName: 'João Silva', isExternal: false,
            dateTimeAttend: null, closeDateTime: null, updatedAt: '2024-03-01T10:00:00Z',
            responsability: null, requestDescription: null,
          },
          {
            source: 'Planning', id: 77, serviceOrderType: 6, equipmentId: 8,
            departmentId: 3, institutionId: 10, unitId: 2,
            scheduledDate: '2024-05-15T00:00:00Z', createdAt: '2024-03-05T09:00:00Z',
            number: null, name: null, status: null, workshopId: null,
            responsibleName: null, isExternal: null, dateTimeAttend: null,
            closeDateTime: null, updatedAt: null,
            responsability: 'Interna', requestDescription: 'Calibração programada anual',
          },
        ],
        pagination: { page: 1, pageSize: 20, totalItems: 2, totalPages: 1, hasMore: false },
      }, null, 2),
    }
  },
  computed: {
    baseUrl() {
      return process.env.VUE_APP_BASE_URL || 'https://api.equipacare.com.br/'
    },
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    scrollTo(id) {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        this.activeSection = id
      }
    },
    onScroll() {
      for (const section of [...this.sections].reverse()) {
        const el = document.getElementById(section.id)
        if (el && el.getBoundingClientRect().top <= 120) {
          this.activeSection = section.id
          return
        }
      }
    },
  },
}
</script>

<style scoped>
/* Navegação */
.docs-nav {
  position: sticky;
  top: 80px;
  background: #f8f9fc;
  border-radius: 10px;
  padding: 16px 12px;
  border: 1px solid #e8ecf0;
}

.nav-label {
  font-size: 0.68rem;
  font-weight: 700;
  color: #9e9e9e;
  letter-spacing: 0.12em;
  padding: 0 8px;
}

.nav-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  border-left: 3px solid transparent;
}

.nav-item:hover {
  background: #fff;
  border-left-color: #e0e0e0;
}

.nav-item--active {
  background: #fff;
  border-left-color: #bbcf20;
}

.nav-item--active .nav-item-text {
  color: #0f3565;
  font-weight: 600;
}

.nav-item--active .nav-item-icon {
  color: #0f3565 !important;
}

.nav-item-icon {
  color: #9e9e9e !important;
  flex-shrink: 0;
}

.nav-item-text {
  font-size: 0.82rem;
  color: #616161;
}

/* Seções */
.doc-section {
  scroll-margin-top: 80px;
}

.section-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f3565;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e8ecf0;
  margin-left: 4px;
}

.doc-text {
  font-size: 0.875rem;
  color: #424242;
  line-height: 1.6;
}

/* Aviso info */
.info-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #e8f0fe;
  border-left: 3px solid #0f3565;
  border-radius: 4px;
  padding: 10px 12px;
  color: #0f3565;
}

.notice-icon {
  margin-top: 1px;
  flex-shrink: 0;
}

.info-notice span {
  font-size: 0.875rem;
  line-height: 1.5;
}

/* Blocos de código */
.code-label {
  font-size: 0.7rem;
  font-weight: 700;
  color: #9e9e9e;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.code-block {
  background: #1e1e1e;
  border-radius: 6px;
  padding: 14px 16px;
  overflow-x: auto;
}

.code-block pre {
  color: #d4d4d4;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.8rem;
  margin: 0;
  white-space: pre;
}

.inline-code {
  background: #f0f0f0;
  padding: 1px 6px;
  border-radius: 3px;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.82em;
  color: #0f3565;
}

/* Endpoint */
.endpoint-block {
  background: #f8f9fc;
  border: 1px solid #e8ecf0;
  border-radius: 8px;
  padding: 16px 20px;
}

.endpoint-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.method-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  font-family: 'Consolas', 'Monaco', monospace;
}

.method-badge.get {
  background: #0f3565;
  color: #fff;
}

.endpoint-url {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.875rem;
  background: #f5f5f5;
  padding: 4px 10px;
  border-radius: 4px;
  color: #202020;
}

/* Tabelas */
.param-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #424242;
}

.doc-table {
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.doc-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.doc-table thead tr {
  background: #4c6d97;
}

.doc-table thead th {
  color: #fff;
  font-weight: 600;
  font-size: 0.78rem;
  padding: 8px 12px;
  text-align: left;
  white-space: nowrap;
}

.doc-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
}

.doc-table tbody tr:last-child {
  border-bottom: none;
}

.doc-table tbody td {
  padding: 8px 12px;
  color: #424242;
  font-weight: 300;
}

.type-col {
  color: #2a8ea4 !important;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.78rem !important;
  white-space: nowrap;
}
</style>
